import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, AlertCircle, CalendarClock, CheckCircle2, RefreshCw, Send, Link as LinkIcon, X, History, ExternalLink, UserPlus, Users, ClipboardList } from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import { useGlobalViewMode } from "../components/ViewModeToggle";

const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUS_COLORS = {
  Open: "bg-slate-100 text-slate-700",
  InProgress: "bg-blue-100 text-blue-700",
  SubmittedForReview: "bg-amber-100 text-amber-800",
  Completed: "bg-green-100 text-green-700",
  Reopened: "bg-red-100 text-red-700",
};

function todayStr() { return new Date().toISOString().slice(0, 10); }

export default function Tasks({ actionPulse = null } = {}) {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [scope, setScope] = useState("mine");
  const [statusFilter, setStatusFilter] = useState("active");
  const [tasks, setTasks] = useState([]);
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "Medium", deadline: "", assigned_to_id: "" });
  // Action dialog state
  const [actionTask, setActionTask] = useState(null);
  const [actionType, setActionType] = useState(null); // propose_deadline | submit | reopen
  const [actionDeadline, setActionDeadline] = useState("");
  const [actionRemark, setActionRemark] = useState("");
  const [submissionUrls, setSubmissionUrls] = useState([""]);
  // History dialog
  const [historyTask, setHistoryTask] = useState(null);
  // Collaborator dialog
  const [collabTask, setCollabTask] = useState(null);
  const [collabPicks, setCollabPicks] = useState([]); // user_ids being added

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/tasks?scope=${scope}`); setTasks(data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/directory").then(({ data }) => setPeople(data)).catch(() => {}); }, []);

  // v109.9.55 — Dashboard "New Task" quick-action deep-link.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }
     
  }, [searchParams]);

  // v109.9.56 — Prop-based actionPulse (opened as modal from Dashboard).
  useEffect(() => {
    if (actionPulse && actionPulse.key === "new") setOpen(true);
  }, [actionPulse]);

  const filteredTasks = useMemo(() => tasks.filter((t) => {
    const s = t.status;
    if (statusFilter === "active") return s !== "Completed" && s !== "Cancelled" && s !== "Rejected";
    if (statusFilter === "completed") return s === "Completed";
    if (statusFilter === "other") return s === "Cancelled" || s === "Rejected" || s === "SubmittedForReview";
    return true;
  }), [tasks, statusFilter]);

  const submit = async () => {
    if (!form.title.trim() || !form.deadline || !form.assigned_to_id) { toast.error("Title, deadline and assignee are required"); return; }
    try {
      await api.post("/tasks", form);
      toast.success("Task assigned");
      setForm({ title: "", description: "", priority: "Medium", deadline: "", assigned_to_id: "" });
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const act = async (task, action, extra = {}) => {
    try { await api.patch(`/tasks/${task.id}`, { action, ...extra }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openCollab = (t) => { setCollabTask(t); setCollabPicks([]); };
  const addCollabs = async () => {
    if (!collabPicks.length) return toast.error("Pick at least one user");
    try {
      await api.post(`/tasks/${collabTask.id}/collaborators`, { user_ids: collabPicks });
      toast.success("Collaborator(s) added");
      setCollabTask(null); setCollabPicks([]); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const removeCollab = async (task, userId) => {
    if (!window.confirm("Remove this collaborator?")) return;
    try {
      await api.delete(`/tasks/${task.id}/collaborators/${userId}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openAction = (t, type) => {
    setActionTask(t); setActionType(type);
    setActionDeadline(type === "propose_deadline" ? (t.deadline || todayStr()) : "");
    setActionRemark("");
    setSubmissionUrls([""]);
  };

  const doAction = async () => {
    if (!actionTask || !actionType) return;
    if (actionType === "propose_deadline") {
      if (!actionDeadline) return toast.error("Pick a new deadline");
      if (!actionRemark.trim()) return toast.error("A remark is required when changing the deadline");
      await act(actionTask, "propose_deadline", { proposed_deadline: actionDeadline, deadline_remark: actionRemark.trim() });
    } else if (actionType === "reopen") {
      await act(actionTask, "reopen", { reviewer_comment: actionRemark });
    } else if (actionType === "submit") {
      const urls = submissionUrls.map((u) => u.trim()).filter(Boolean);
      // Validate URLs lightly
      const bad = urls.find((u) => !/^https?:\/\//i.test(u));
      if (bad) return toast.error(`URL must start with http:// or https:// — "${bad}"`);
      await act(actionTask, "submit", { submission_remark: actionRemark, submission_urls: urls });
    }
    setActionTask(null); setActionType(null); setActionRemark(""); setActionDeadline(""); setSubmissionUrls([""]);
  };

  return (
    <div className="max-w-[1400px] space-y-6">
      <PageHeader
        tone="violet"
        icon={ClipboardList}
        eyebrow="Workspace"
        title="Tasks"
        subtitle="Assign work to anyone, track deadlines, review submissions."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-br from-violet-600 to-fuchsia-700 hover:from-violet-700 hover:to-fuchsia-800 text-white h-10 shadow-sm" data-testid="task-add-button">
                <Plus size={16} strokeWidth={1.75} className="mr-1.5" /> Assign Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle className="heading">Assign New Task</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
              <Field label="Title *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" /></Field>
              <Field label="Description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Priority *">
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Deadline *">
                  <Input type="date" min={todayStr()} value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="task-deadline" />
                </Field>
              </div>
              <Field label="Assign To *">
                <Select value={form.assigned_to_id} onValueChange={(v) => setForm({ ...form, assigned_to_id: v })}>
                  <SelectTrigger data-testid="task-assignee"><SelectValue placeholder="Choose a person…" /></SelectTrigger>
                  <SelectContent>
                    {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.employee_code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="task-submit">Assign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        }
      />

      <Tabs value={scope} onValueChange={setScope}>
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-mine">Assigned to me</TabsTrigger>
          <TabsTrigger value="created" data-testid="tab-created">Created by me</TabsTrigger>
          {isMaster && <TabsTrigger value="all" data-testid="tab-all">All Tasks</TabsTrigger>}
        </TabsList>
        <TabsContent value={scope} className="mt-4">
          <div className="flex items-center gap-1.5 mb-3" data-testid="task-status-filters">
            <FilterChip label={`Active (${tasks.filter((t) => t.status !== "Completed" && t.status !== "Cancelled" && t.status !== "Rejected").length})`} active={statusFilter === "active"} onClick={() => setStatusFilter("active")} testid="task-filter-active" />
            <FilterChip label={`Completed (${tasks.filter((t) => t.status === "Completed").length})`} active={statusFilter === "completed"} onClick={() => setStatusFilter("completed")} testid="task-filter-completed" />
            <FilterChip label={`Other (${tasks.filter((t) => t.status === "Cancelled" || t.status === "Rejected" || t.status === "SubmittedForReview").length})`} active={statusFilter === "other"} onClick={() => setStatusFilter("other")} testid="task-filter-other" />
          </div>
          <TaskList tasks={filteredTasks} user={user} onAct={act} onOpenAction={openAction} onShowHistory={(t) => setHistoryTask(t)} onOpenCollab={openCollab} onRemoveCollab={removeCollab} />
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      <Dialog open={!!actionTask} onOpenChange={(o) => { if (!o) { setActionTask(null); setActionType(null); } }}>
        <DialogContent className="max-w-md" data-testid="task-action-dialog">
          <DialogHeader>
            <DialogTitle className="heading">
              {actionType === "propose_deadline" ? "Change Deadline" :
               actionType === "submit" ? "Submit for Approval" :
               "Reopen — reason"}
            </DialogTitle>
          </DialogHeader>
          {actionType === "propose_deadline" && (
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">New Deadline *</Label>
                <Input type="date" min={todayStr()} value={actionDeadline} onChange={(e) => setActionDeadline(e.target.value)} className="mt-1.5" data-testid="action-deadline" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Reason / Remark *</Label>
                <Textarea rows={3} value={actionRemark} onChange={(e) => setActionRemark(e.target.value)} placeholder="Why is the deadline being changed?" className="mt-1.5" data-testid="action-remark" />
                <div className="text-[10px] text-amber-700 mt-1">Required — visible in this task&apos;s deadline history.</div>
              </div>
            </div>
          )}
          {actionType === "reopen" && (
            <Textarea rows={3} value={actionRemark} onChange={(e) => setActionRemark(e.target.value)} placeholder="What's missing or needs rework?" data-testid="action-reason" />
          )}
          {actionType === "submit" && (
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Submission Remark (optional)</Label>
                <Textarea rows={3} value={actionRemark} onChange={(e) => setActionRemark(e.target.value)} placeholder="Anything the reviewer should know…" className="mt-1.5" data-testid="action-submit-remark" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1"><LinkIcon size={11}/> Deliverable URLs (optional)</Label>
                <div className="mt-1.5 space-y-2">
                  {submissionUrls.map((u, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input value={u} onChange={(e) => { const arr = [...submissionUrls]; arr[i] = e.target.value; setSubmissionUrls(arr); }} placeholder="https://drive.google.com/…" data-testid={`submission-url-${i}`} />
                      {submissionUrls.length > 1 && (
                        <Button size="sm" variant="outline" onClick={() => setSubmissionUrls(submissionUrls.filter((_, idx) => idx !== i))} className="h-9 px-2" data-testid={`submission-url-remove-${i}`}><X size={12}/></Button>
                      )}
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setSubmissionUrls([...submissionUrls, ""])} className="h-8 text-xs" data-testid="submission-url-add">
                    <Plus size={11} className="mr-1"/> Add another link
                  </Button>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">e.g., Drive file, GitHub PR, deployed preview, design doc.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionTask(null); setActionType(null); }}>Cancel</Button>
            <Button onClick={doAction} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="action-confirm">
              {actionType === "submit" ? "Submit for Approval" : actionType === "propose_deadline" ? "Update Deadline" : "Reopen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deadline History Dialog */}
      <Dialog open={!!historyTask} onOpenChange={(o) => { if (!o) setHistoryTask(null); }}>
        <DialogContent className="max-w-lg" data-testid="task-history-dialog">
          <DialogHeader><DialogTitle className="heading flex items-center gap-2"><History size={16}/> Deadline History</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">{historyTask?.title}</div>
          <div className="max-h-[60vh] overflow-y-auto space-y-3">
            {(!historyTask?.deadline_history || historyTask.deadline_history.length === 0) && (
              <div className="text-sm text-slate-400 italic py-6 text-center">No deadline changes yet.</div>
            )}
            {historyTask?.deadline_history?.map((h, i) => (
              <div key={i} className="border-l-2 border-amber-300 pl-3 py-1" data-testid={`task-history-entry-${i}`}>
                <div className="text-[11px] mono text-slate-600">
                  <span className="line-through text-slate-400">{h.old_deadline || "—"}</span> → <span className="font-semibold text-slate-900">{h.new_deadline}</span>
                </div>
                <div className="text-sm text-slate-800 mt-1">{h.remark}</div>
                <div className="text-[10px] text-slate-500 mt-1">— {h.changed_by_name} <span className="text-slate-400">· {h.changed_by_role}</span> · {h.changed_at ? new Date(h.changed_at).toLocaleString() : ""}</div>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setHistoryTask(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collaborator Dialog */}
      <Dialog open={!!collabTask} onOpenChange={(o) => { if (!o) { setCollabTask(null); setCollabPicks([]); } }}>
        <DialogContent className="max-w-md" data-testid="task-collab-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2"><UserPlus size={16}/> Add Collaborators</DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">{collabTask?.title}</div>
            <div className="text-[10px] text-slate-500">Collaborators can change the deadline and submit the task — same powers as the assignee. The list locks once the task is approved.</div>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Select people ({collabPicks.length})</Label>
            <div className="mt-1.5 max-h-64 overflow-y-auto border border-slate-200 rounded-md p-2 space-y-1">
              {people
                .filter((p) => p.id !== collabTask?.assigned_to_id && !(collabTask?.collaborators || []).some((c) => c.user_id === p.id))
                .map((p) => (
                  <label key={p.id} className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer" data-testid={`collab-pick-${p.id}`}>
                    <input type="checkbox" checked={collabPicks.includes(p.id)}
                      onChange={() => setCollabPicks(collabPicks.includes(p.id) ? collabPicks.filter((x) => x !== p.id) : [...collabPicks, p.id])}
                      className="rounded" />
                    <span className="text-sm flex-1">{p.name}</span>
                    <span className="text-[10px] text-slate-500 mono">{p.employee_code || p.email}</span>
                  </label>
                ))}
              {people.filter((p) => p.id !== collabTask?.assigned_to_id && !(collabTask?.collaborators || []).some((c) => c.user_id === p.id)).length === 0 && (
                <div className="text-xs italic text-slate-400 px-3 py-3">Everyone&apos;s already on this task.</div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCollabTask(null); setCollabPicks([]); }}>Cancel</Button>
            <Button onClick={addCollabs} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="collab-add-confirm">Add {collabPicks.length > 0 ? `(${collabPicks.length})` : ""}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskList({ tasks, user, onAct, onOpenAction, onShowHistory, onOpenCollab, onRemoveCollab }) {
  const [viewMode] = useGlobalViewMode();
  if (tasks.length === 0) return <div className="pk-card p-12 text-center text-slate-400 text-sm">No tasks here.</div>;
  const uid = user?.id;

  if (viewMode === "list") {
    return (
      <div className="pk-card overflow-x-auto" data-testid="task-list-table">
        <table className="w-full text-sm responsive-table">
          <thead className="bg-slate-50/80 border-b border-slate-200">
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-semibold">Task</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Priority</th>
              <th className="px-4 py-3 font-semibold">Assignee</th>
              <th className="px-4 py-3 font-semibold">Assigner</th>
              <th className="px-4 py-3 font-semibold">Due</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => {
              const isAssignee = t.assigned_to_id === uid;
              const isCreator = t.created_by_id === uid;
              const isMaster = user?.role === "master_admin";
              const collabs = t.collaborators || [];
              const isCollaborator = collabs.some((c) => c.user_id === uid);
              const isWorker = isAssignee || isCollaborator;
              const canChangeDeadline = (isWorker || isCreator || isMaster) && t.status !== "Completed" && t.status !== "SubmittedForReview";
              const canAddCollab = (isAssignee || isCollaborator || isMaster) && t.status !== "Completed";
              return (
                <tr key={t.id} className={`border-b last:border-0 hover:bg-slate-50/60 ${t.overdue ? "bg-red-50/40" : ""}`} data-testid={`task-row-${t.id}`}>
                  <td data-label="Task" className="px-4 py-3 align-top">
                    <div className="font-semibold text-slate-900 text-[13.5px]">{t.title}</div>
                    {t.description && <div className="text-[11.5px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>}
                    {collabs.length > 0 && (
                      <div className="mt-1 flex items-center gap-1 flex-wrap">
                        <Users size={10} className="text-violet-500" />
                        {collabs.map((c) => (
                          <span key={c.user_id} className="text-[10px] px-1.5 py-0.5 bg-violet-50 border border-violet-200 rounded text-violet-800">{c.user_name}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td data-label="Status" className="px-4 py-3 align-top">
                    <Badge className={STATUS_COLORS[t.status] || "bg-slate-100 text-slate-700"}>{t.status}</Badge>
                    {t.overdue && <div className="mt-1"><Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[9.5px] flex items-center gap-1 w-fit"><AlertCircle size={9}/> Overdue</Badge></div>}
                  </td>
                  <td data-label="Priority" className="px-4 py-3 align-top">
                    <Badge variant="outline" className={`text-[10px] ${t.priority === "Critical" ? "border-red-300 text-red-700" : t.priority === "High" ? "border-amber-300 text-amber-700" : "border-slate-200 text-slate-600"}`}>{t.priority}</Badge>
                  </td>
                  <td data-label="Assignee" className="px-4 py-3 align-top text-[12px]">{t.assigned_to_name}</td>
                  <td data-label="Assigner" className="px-4 py-3 align-top text-[12px] text-slate-600">{t.created_by_name}</td>
                  <td data-label="Due" className="px-4 py-3 align-top mono text-[11.5px] text-slate-700">{t.deadline}</td>
                  <td data-label="Actions" className="px-4 py-3 align-top">
                    <div className="flex flex-wrap gap-1 justify-end">
                      {canAddCollab && (
                        <Button size="sm" variant="outline" className="h-7 text-[10.5px] text-violet-700 border-violet-200 hover:bg-violet-50" onClick={() => onOpenCollab(t)} data-testid={`task-add-collab-${t.id}`}>
                          <UserPlus size={10} className="mr-1"/> Collab
                        </Button>
                      )}
                      {canChangeDeadline && (
                        <Button size="sm" variant="outline" className="h-7 text-[10.5px]" onClick={() => onOpenAction(t, "propose_deadline")} data-testid={`task-propose-${t.id}`}>
                          <CalendarClock size={10} className="mr-1" /> Deadline
                        </Button>
                      )}
                      {(isWorker || isMaster) && t.status !== "Completed" && t.status !== "SubmittedForReview" && (
                        <Button size="sm" className="h-7 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onOpenAction(t, "submit")} data-testid={`task-submit-action-${t.id}`}>
                          <Send size={10} className="mr-1" /> Submit
                        </Button>
                      )}
                      {(isCreator || isMaster) && t.status === "SubmittedForReview" && (
                        <>
                          <Button size="sm" className="h-7 text-[10.5px] bg-green-600 hover:bg-green-700 text-white" onClick={() => onAct(t, "approve")} data-testid={`task-approve-${t.id}`}>
                            <CheckCircle2 size={10} className="mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[10.5px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => onOpenAction(t, "reopen")} data-testid={`task-reopen-${t.id}`}>
                            <RefreshCw size={10} className="mr-1" /> Reopen
                          </Button>
                        </>
                      )}
                      {(t.deadline_history?.length || 0) > 0 && (
                        <Button size="sm" variant="outline" className="h-7 text-[10.5px] text-amber-700 border-amber-200 hover:bg-amber-50" onClick={() => onShowHistory(t)} data-testid={`task-history-button-${t.id}`}>
                          <History size={10} className="mr-1"/> {t.deadline_history.length}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="task-list">
      {tasks.map((t) => {
        const isAssignee = t.assigned_to_id === uid;
        const isCreator = t.created_by_id === uid;
        const isMaster = user?.role === "master_admin";
        const collabs = t.collaborators || [];
        const isCollaborator = collabs.some((c) => c.user_id === uid);
        const isWorker = isAssignee || isCollaborator;
        const canChangeDeadline = (isWorker || isCreator || isMaster) && t.status !== "Completed" && t.status !== "SubmittedForReview";
        const canAddCollab = (isAssignee || isCollaborator || isMaster) && t.status !== "Completed";
        const historyCount = t.deadline_history?.length || 0;
        return (
          <div key={t.id} className={`pk-card p-4 hover:shadow-md transition-shadow flex flex-col ${t.overdue ? "border-red-300 bg-red-50/30" : ""}`} data-testid={`task-row-${t.id}`}>
            {/* Header — title + status badges */}
            <div className="mb-2">
              <div className="font-bold text-slate-900 text-[14.5px] leading-tight line-clamp-2 mb-1.5">{t.title}</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge className={(STATUS_COLORS[t.status] || "bg-slate-100 text-slate-700") + " text-[10px]"}>{t.status}</Badge>
                <Badge variant="outline" className={`text-[10px] ${t.priority === "Critical" ? "border-red-300 text-red-700" : t.priority === "High" ? "border-amber-300 text-amber-700" : "border-slate-200 text-slate-600"}`}>{t.priority}</Badge>
                {t.overdue && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 flex items-center gap-1 text-[10px]"><AlertCircle size={10} strokeWidth={2} /> Overdue</Badge>}
                {isCollaborator && <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 text-[10px]">Collab</Badge>}
              </div>
            </div>

            {/* Description */}
            {t.description && <p className="text-[12px] text-slate-600 line-clamp-2 mb-2">{t.description}</p>}

            {/* Meta grid */}
            <div className="grid grid-cols-2 gap-2 text-[11px] mb-2">
              <div>
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Assignee</div>
                <div className="text-slate-800 truncate">{t.assigned_to_name}</div>
              </div>
              <div>
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Assigner</div>
                <div className="text-slate-800 truncate">{t.created_by_name}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Due</div>
                <div className="mono text-slate-800 flex items-center gap-1">
                  <CalendarClock size={11} /> {t.deadline}
                  {historyCount > 0 && (
                    <button onClick={() => onShowHistory(t)} className="ml-1 text-[10px] text-amber-700 hover:text-amber-900 underline underline-offset-2" data-testid={`task-history-button-${t.id}`}>
                      · {historyCount} change{historyCount > 1 ? "s" : ""}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Collaborator pills */}
            {collabs.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap mb-2" data-testid={`task-collabs-${t.id}`}>
                <Users size={10} className="text-violet-500"/>
                {collabs.map((c) => (
                  <span key={c.user_id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-violet-50 border border-violet-200 rounded-full text-violet-800" data-testid={`task-collab-${t.id}-${c.user_id}`}>
                    {c.user_name}
                    {(isAssignee || isMaster) && t.status !== "Completed" && (
                      <button onClick={() => onRemoveCollab(t, c.user_id)} className="text-violet-400 hover:text-red-600" data-testid={`task-collab-remove-${t.id}-${c.user_id}`}><X size={9}/></button>
                    )}
                  </span>
                ))}
              </div>
            )}

            {/* Notes / submission remarks */}
            {t.reviewer_comment && <div className="text-[11.5px] text-slate-700 bg-slate-50 border-l-2 border-amber-300 px-2 py-1 rounded mb-1.5 line-clamp-2">Review: {t.reviewer_comment}</div>}
            {t.submission_remark && <div className="text-[11.5px] text-slate-700 bg-slate-50 border-l-2 border-emerald-300 px-2 py-1 rounded mb-1.5 line-clamp-2" data-testid={`task-submission-remark-${t.id}`}>Submission: {t.submission_remark}</div>}
            {t.submission_urls?.length > 0 && (
              <div className="bg-emerald-50/50 border-l-2 border-emerald-400 px-2 py-1.5 rounded space-y-0.5 mb-2" data-testid={`task-submission-urls-${t.id}`}>
                <div className="text-[9.5px] uppercase tracking-wider font-semibold text-emerald-700 flex items-center gap-1"><LinkIcon size={9}/> Links</div>
                {t.submission_urls.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[11px] text-blue-700 hover:underline break-all line-clamp-1" data-testid={`task-submission-url-${t.id}-${i}`}>
                    <ExternalLink size={9}/> {u}
                  </a>
                ))}
              </div>
            )}

            {/* Actions — bottom */}
            <div className="mt-auto flex flex-wrap gap-1 pt-2 border-t border-slate-100">
              {canAddCollab && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] text-violet-700 border-violet-200 hover:bg-violet-50" onClick={() => onOpenCollab(t)} data-testid={`task-add-collab-${t.id}`}>
                  <UserPlus size={10} className="mr-1"/> Collab
                </Button>
              )}
              {canChangeDeadline && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" onClick={() => onOpenAction(t, "propose_deadline")} data-testid={`task-propose-${t.id}`}>
                  <CalendarClock size={10} className="mr-1" /> Deadline
                </Button>
              )}
              {(isWorker || isMaster) && t.status !== "Completed" && t.status !== "SubmittedForReview" && (
                <Button size="sm" className="h-7 px-2 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onOpenAction(t, "submit")} data-testid={`task-submit-action-${t.id}`}>
                  <Send size={10} className="mr-1" /> Submit
                </Button>
              )}
              {(isCreator || isMaster) && t.status === "SubmittedForReview" && (
                <>
                  <Button size="sm" className="h-7 px-2 text-[10.5px] bg-green-600 hover:bg-green-700 text-white" onClick={() => onAct(t, "approve")} data-testid={`task-approve-${t.id}`}>
                    <CheckCircle2 size={10} className="mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => onOpenAction(t, "reopen")} data-testid={`task-reopen-${t.id}`}>
                    <RefreshCw size={10} className="mr-1" /> Not Done
                  </Button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button onClick={onClick} className={`h-8 px-3 text-[11px] font-semibold rounded-full border transition-all ${active ? "bg-[var(--pk-accent)] border-[var(--pk-accent)] text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={testid}>
      {label}
    </button>
  );
}
